// MCP Security — Reports page (slice 4, wired).
//
// Top: table of the 4 canonical report types (weekly_digest,
// soc2_evidence, ai_agent_audit, coverage_matrix) with "+ New schedule"
// to open the editor modal.
// Middle: schedules table for the current site — enabled toggle, cron,
// next-fire ETA, "Trigger now", "Run history" expand, edit + delete.
// Bottom (per expand): McpReportRun rows with status pill + download.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";

import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import axios from "../../../../../utils/axios/v1/userAxios";
import tokens from "./_shared/tokens";
import { primaryButtonStyle, secondaryButtonStyle } from "./_shared/buttonStyles";

// ── Canonical report types (shape mirrors backend REPORT_TYPES) ────────
const REPORT_TYPES = [
  {
    slug: "weekly_digest",
    name: "Weekly digest",
    aud: "Execs",
    desc: "Last 7 days: top charts (ASCII in MD, bar in PDF), top events, policy-drift summary.",
    formats: ["PDF + MD"],
    deliveries: ["s3", "email", "jira"],
  },
  {
    slug: "soc2_evidence",
    name: "SOC2 evidence pack",
    aud: "Compliance",
    desc: "Every McpSettingChange + McpEndpointAudit in the window. Deterministic, reproducible.",
    formats: ["CSV + PDF"],
    deliveries: ["s3", "email"],
  },
  {
    slug: "ai_agent_audit",
    name: "AI agent audit",
    aud: "SecOps",
    desc: "Per-agent breakdown: calls, outcomes, scopes, risk-weight history.",
    formats: ["CSV + JSONL"],
    deliveries: ["s3", "email", "jira"],
  },
  {
    slug: "coverage_matrix",
    name: "Coverage matrix",
    aud: "GRC",
    desc: "Static snapshot of the attack-coverage matrix (hard-coded template).",
    formats: ["HTML"],
    deliveries: ["s3", "email"],
  },
];

const STATUS_COLOR = {
  running: tokens.neutral[700],
  success: tokens.success?.[600] || "#1DD75B",
  failed: tokens.danger?.[600] || "#D00416",
};

// ── Component ─────────────────────────────────────────────────────────
export default function McpReports() {
  const { effectiveSiteUid } = useGlobalSite();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null); // null | {mode:'new'|'edit', slug?, cron?, delivery?, id?}

  const refresh = useCallback(async () => {
    if (!effectiveSiteUid) return;
    setLoading(true);
    setErr(null);
    try {
      const resp = await axios.get(`mcp/sites/${effectiveSiteUid}/reports`);
      setSchedules(resp?.data?.data?.schedules || []);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "failed to load schedules");
    } finally {
      setLoading(false);
    }
  }, [effectiveSiteUid]);

  useEffect(() => { refresh(); }, [refresh]);

  const createSchedule = async (payload) => {
    const resp = await axios.post(`mcp/sites/${effectiveSiteUid}/reports/schedule`, payload);
    await refresh();
    return resp;
  };
  const patchSchedule = async (id, payload) => {
    await axios.patch(`mcp/sites/${effectiveSiteUid}/reports/schedule/${id}`, payload);
    await refresh();
  };
  const deleteSchedule = async (id) => {
    if (!window.confirm("Delete this schedule and all its run history?")) return;
    await axios.delete(`mcp/sites/${effectiveSiteUid}/reports/schedule/${id}`);
    await refresh();
  };
  const triggerRun = async (id) => {
    try {
      await axios.post(`mcp/sites/${effectiveSiteUid}/reports/schedule/${id}/run`, {});
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.message || "trigger failed");
    }
  };

  return (
    <>
      <Helmet title="MCP Security — Reports" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Security — Reports"
          description="Generate on-demand or schedule recurring MCP reports — delivered to S3, email, or Jira."
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
              {!effectiveSiteUid && (
                <Alert severity="info">Select a site from the header to view reports.</Alert>
              )}
              {err && <Alert severity="error">{err}</Alert>}

              {/* ── Report types ──────────────────────────────────── */}
              <Box
                sx={{
                  background: "#fff",
                  border: `1px solid ${tokens.neutral[200]}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ p: 1.75, borderBottom: `1px solid ${tokens.neutral[200]}`, display: "flex", alignItems: "center" }}>
                  <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: tokens.neutral[1000], flex: 1 }}>Available report types</Typography>
                  <Button
                    sx={primaryButtonStyle}
                    disabled={!effectiveSiteUid}
                    onClick={() => setModal({ mode: "new", slug: "weekly_digest", cron: "0 9 * * MON", delivery: JSON.stringify({ type: "s3" }, null, 2) })}
                  >
                    + New schedule
                  </Button>
                </Box>
                <Box sx={{ overflowX: "auto" }}>
                  <Box sx={{ minWidth: 820 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "180px 120px minmax(220px, 1fr) 120px 180px",
                        gap: "0 12px",
                        px: 2.5, py: 1.25,
                        background: tokens.neutral[50],
                        borderBottom: `1px solid ${tokens.neutral[200]}`,
                        fontSize: 11, fontWeight: 500, color: tokens.neutral[700],
                        textTransform: "uppercase", letterSpacing: "0.04em",
                      }}
                    >
                      <span>Report</span><span>Audience</span><span>Description</span><span>Format</span><span>Delivery options</span>
                    </Box>
                    {REPORT_TYPES.map((r) => (
                      <Box
                        key={r.slug}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "180px 120px minmax(220px, 1fr) 120px 180px",
                          gap: "0 12px",
                          px: 2.5, py: 1.25,
                          alignItems: "center",
                          borderBottom: `1px solid ${tokens.neutral[150]}`,
                          fontSize: 13,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <AssessmentOutlinedIcon fontSize="inherit" sx={{ color: tokens.primaryIndigo }} />
                          <span style={{ fontFamily: tokens.fontMono, fontSize: 12 }}>{r.slug}</span>
                        </Stack>
                        <span style={{ color: tokens.neutral[700] }}>{r.aud}</span>
                        <span style={{ color: tokens.neutral[800], fontSize: 12 }}>{r.desc}</span>
                        <span style={{ fontFamily: tokens.fontMono, fontSize: 11, color: tokens.neutral[700] }}>{r.formats.join(", ")}</span>
                        <span style={{ fontFamily: tokens.fontMono, fontSize: 11, color: tokens.neutral[700] }}>{r.deliveries.join(", ")}</span>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* ── Schedules ──────────────────────────────────────── */}
              <Box
                sx={{
                  background: "#fff",
                  border: `1px solid ${tokens.neutral[200]}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ p: 1.75, borderBottom: `1px solid ${tokens.neutral[200]}`, display: "flex" }}>
                  <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: tokens.neutral[1000], flex: 1 }}>Schedules for this site</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.neutral[600] }}>
                    {loading ? "loading..." : `${schedules.length} schedule(s)`}
                  </Typography>
                </Box>
                {schedules.length === 0 && !loading && (
                  <Box sx={{ p: 2.5, color: tokens.neutral[600], fontSize: 13 }}>
                    No schedules yet. Click "+ New schedule" to create one.
                  </Box>
                )}
                {schedules.map((s) => (
                  <ScheduleRow
                    key={s.id}
                    s={s}
                    siteUid={effectiveSiteUid}
                    onToggle={() => patchSchedule(s.id, { enabled: !s.enabled })}
                    onEdit={() => setModal({
                      mode: "edit", id: s.id, slug: s.slug,
                      cron: s.cron, delivery: JSON.stringify(s.delivery || {}, null, 2),
                    })}
                    onDelete={() => deleteSchedule(s.id)}
                    onTrigger={() => triggerRun(s.id)}
                  />
                ))}
              </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>

      <ScheduleEditor
        modal={modal}
        onClose={() => setModal(null)}
        onCreate={createSchedule}
        onPatch={patchSchedule}
      />
    </>
  );
}

// ── Schedule row (with expandable run history) ───────────────────────
function ScheduleRow({ s, siteUid, onToggle, onEdit, onDelete, onTrigger }) {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const nextEta = useMemo(() => cronEtaLabel(s.cron, s.last_run_at), [s.cron, s.last_run_at]);

  const loadRuns = useCallback(async () => {
    if (!siteUid) return;
    setLoadingRuns(true);
    try {
      const resp = await axios.get(`mcp/sites/${siteUid}/reports/runs?schedule_id=${s.id}&limit=20`);
      setRuns(resp?.data?.items || []);
    } catch (e) {
      setRuns([]);
    } finally {
      setLoadingRuns(false);
    }
  }, [siteUid, s.id]);

  const toggleOpen = () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) loadRuns();
  };

  const download = async (runId) => {
    try {
      const resp = await axios.get(`mcp/sites/${siteUid}/reports/runs/${runId}`);
      const url = resp?.data?.data?.download_url;
      if (!url) {
        alert(resp?.data?.data?.status ? `status: ${resp.data.data.status}` : "no download URL available");
        return;
      }
      window.open(url, "_blank", "noopener");
    } catch (e) {
      alert(e?.response?.data?.message || "download failed");
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "32px 160px 100px 180px minmax(140px, 1fr) 220px",
          gap: "0 12px",
          px: 2.5, py: 1.25,
          alignItems: "center",
          borderBottom: `1px solid ${tokens.neutral[150]}`,
          fontSize: 13,
        }}
      >
        <IconButton size="small" onClick={toggleOpen}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <span style={{ fontFamily: tokens.fontMono, fontSize: 12 }}>{s.slug}</span>
        <Switch size="small" checked={!!s.enabled} onChange={onToggle} />
        <span style={{ fontFamily: tokens.fontMono, fontSize: 11, color: tokens.neutral[800] }} title={s.cron}>
          {s.cron}
        </span>
        <span style={{ color: tokens.neutral[700], fontSize: 12 }}>{nextEta}</span>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Trigger now">
            <IconButton size="small" onClick={onTrigger}><PlayArrowOutlinedIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={onEdit}><EditOutlinedIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={onDelete}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      </Box>
      {open && (
        <Box sx={{ background: tokens.neutral[50], px: 4, py: 1.5, borderBottom: `1px solid ${tokens.neutral[150]}` }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 12, color: tokens.neutral[700], flex: 1 }}>
              Run history {loadingRuns ? "(loading...)" : `(${runs.length})`}
            </Typography>
            <Button size="small" onClick={loadRuns} sx={{ fontSize: 11 }}>Refresh</Button>
          </Stack>
          {runs.length === 0 && !loadingRuns && (
            <Typography sx={{ fontSize: 12, color: tokens.neutral[600] }}>No runs yet.</Typography>
          )}
          {runs.map((r) => (
            <Box
              key={r.id}
              sx={{
                display: "grid",
                gridTemplateColumns: "220px 90px 170px 110px minmax(180px,1fr) 110px",
                gap: "0 12px",
                py: 0.75,
                alignItems: "center",
                borderBottom: `1px solid ${tokens.neutral[150]}`,
                fontSize: 12,
              }}
            >
              <span style={{ fontFamily: tokens.fontMono, fontSize: 11, color: tokens.neutral[800] }}>
                {r.id.slice(0, 24)}...
              </span>
              <span
                style={{
                  display: "inline-block",
                  padding: "2px 10px",
                  borderRadius: 999,
                  background: "#fff",
                  color: STATUS_COLOR[r.status] || tokens.neutral[700],
                  border: `1px solid ${STATUS_COLOR[r.status] || tokens.neutral[200]}`,
                  fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "center",
                }}
              >
                {r.status}
              </span>
              <span style={{ color: tokens.neutral[700] }}>
                {r.started_at ? new Date(r.started_at).toLocaleString() : ""}
              </span>
              <span style={{ fontFamily: tokens.fontMono, fontSize: 11, color: tokens.neutral[700] }}>
                {r.output_bytes != null ? humanBytes(r.output_bytes) : ""}
              </span>
              <span style={{ fontSize: 11, color: tokens.neutral[700], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.error || r.output_content_type || ""}>
                {r.error ? `error: ${r.error.slice(0, 80)}` : (r.output_content_type || "")}
              </span>
              {r.status === "success" ? (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer", color: tokens.primary[300] }} onClick={() => download(r.id)}>
                  <DownloadOutlinedIcon fontSize="inherit" />
                  <span>Download</span>
                </Stack>
              ) : (
                <span style={{ color: tokens.neutral[500] }}>—</span>
              )}
            </Box>
          ))}
        </Box>
      )}
    </>
  );
}

// ── Editor modal ─────────────────────────────────────────────────────
function ScheduleEditor({ modal, onClose, onCreate, onPatch }) {
  const [slug, setSlug] = useState("weekly_digest");
  const [cron, setCron] = useState("0 9 * * MON");
  const [deliveryStr, setDeliveryStr] = useState('{"type":"s3"}');
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!modal) return;
    setSlug(modal.slug || "weekly_digest");
    setCron(modal.cron || "0 9 * * MON");
    setDeliveryStr(modal.delivery || '{"type":"s3"}');
    setEnabled(true);
    setErr(null);
  }, [modal]);

  if (!modal) return null;

  const submit = async () => {
    setBusy(true); setErr(null);
    let delivery;
    try { delivery = JSON.parse(deliveryStr); }
    catch (e) { setErr("delivery must be valid JSON"); setBusy(false); return; }
    try {
      if (modal.mode === "new") {
        await onCreate({ slug, cron, delivery });
      } else {
        await onPatch(modal.id, { cron, delivery });
      }
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{modal.mode === "new" ? "New schedule" : "Edit schedule"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {err && <Alert severity="error">{err}</Alert>}
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography sx={{ fontSize: 13, width: 90 }}>Type</Typography>
            <Select
              size="small"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={modal.mode === "edit"}
              fullWidth
            >
              {REPORT_TYPES.map((t) => (
                <MenuItem key={t.slug} value={t.slug}>{t.slug}</MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography sx={{ fontSize: 13, width: 90 }}>Cron</Typography>
            <TextField
              size="small"
              fullWidth
              value={cron}
              onChange={(e) => setCron(e.target.value)}
              helperText='UTC. Use "@manual" to disable scheduled firing (ad-hoc only).'
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Typography sx={{ fontSize: 13, width: 90, pt: 1 }}>Delivery</Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={5}
              value={deliveryStr}
              onChange={(e) => setDeliveryStr(e.target.value)}
              helperText='JSON. Examples: {"type":"s3"}  |  {"type":"email","to":["a@b.com"]}  |  {"type":"jira","project":"SEC","issuetype":"Task","destination_id":"..."}'
              InputProps={{ sx: { fontFamily: tokens.fontMono, fontSize: 12 } }}
            />
          </Stack>
          {modal.mode === "new" && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography sx={{ fontSize: 13, width: 90 }}>Enabled</Typography>
              <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button sx={secondaryButtonStyle} onClick={onClose} disabled={busy}>Cancel</Button>
        <Button sx={primaryButtonStyle} onClick={submit} disabled={busy}>
          {busy ? "Saving..." : (modal.mode === "new" ? "Create" : "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────
function humanBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

// Very loose "next fire ETA" label — we don't ship a cron-parser bundle
// into the UI, so render the raw expression plus a hint. The backend
// surfaces the real last_run_at.
function cronEtaLabel(cron, last_run_at) {
  if (!cron) return "—";
  if (cron === "@manual") return "manual only";
  const last = last_run_at ? new Date(last_run_at) : null;
  if (!last) return "scheduled (never run yet)";
  const mins = Math.max(0, Math.round((Date.now() - last.getTime()) / 60000));
  const label = mins < 60 ? `${mins}m ago`
              : mins < 1440 ? `${Math.round(mins / 60)}h ago`
              : `${Math.round(mins / 1440)}d ago`;
  return `last ran ${label}`;
}
